import { DataRow, Value } from './DataTable';
import { ColumnReference, ColumnRefOrValue, isColumnRef } from './EffectiveDefinition';

export type ValueAccessor<T> = (row: DataRow) => T;

export const accessorForReference = <T extends Value>(columnReference: ColumnReference): ValueAccessor<T> => {
  return (row) => row[columnReference.column] as T;
};

export const accessorForRefOrValue = (refOrValue: ColumnRefOrValue): ValueAccessor<number | string> => {
  if (isColumnRef(refOrValue)) {
    return accessorForReference(refOrValue);
  }

  return (row) => refOrValue;
};

export const throwIfUndefined = <T>(
  accessor: ValueAccessor<T | undefined>,
  message: string = 'Accessor returned undefined'
): ValueAccessor<T> => {
  return (row: DataRow) => {
    const v = accessor(row);
    if (v === undefined) {
      throw new Error(message);
    }
    return v;
  };
};

export const undefinedToNull = <T>(accessor: ValueAccessor<T | undefined>): ValueAccessor<T | null> => {
  return (row: DataRow) => accessor(row) ?? null;
};

export const undefinedToZero = <T>(accessor: ValueAccessor<T | undefined>): ValueAccessor<T | number> => {
  return (row: DataRow) => accessor(row) ?? 0;
};

export type VictoryRow = {
  datum: DataRow;
};

/**
 * In some victory sub components such as labels, functions need to operate on
 * rows of data that have been processed by victory, instead of `DataRow`s
 * directly. In these cases, the original `DataRow` is included under the
 * `datum` key. This helper function wraps any `ValueAccessor`, and allows it to
 * work as if passed a `DataRow` directly.
 *
 * @param accessor the `ValueAccessor` to wrap
 */
export const extractDatumFromVictoryRow = <T>(accessor: ValueAccessor<T>): ((row: VictoryRow) => T) => {
  return (row) => accessor(row.datum);
};

export const DefaultMeasureAccessor: ValueAccessor<number | undefined> = (row) => row.measure as number;
export const DefaultTimestampAccessor: ValueAccessor<Date> = (row) => row.timestamp as Date;
